import React, { useState, useEffect, forwardRef, useRef } from "react";
import {
  FormControl,
  TextField,
  Autocomplete,
  Stack,
  FormHelperText,
} from "@mui/material";
import { db } from "../../Firebase/firebase";
import "./Nolose.css";
import NumberFormat from "react-number-format";
import getWhatsAppUrl from "../../utils/getWhatsAppUrl";
import isValidProductValue from "../../utils/isValidProductValue";
import { getTypeName } from "../../utils/isADevice";
import { useAuth } from "../../AuthContext";
import { useAgentAuth } from "../../AuthAgentContext";
import registerSelection from "../../services/logs/registerSelection";

const NumberFormatInput = forwardRef(function NumberFormatInput(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
      prefix="$"
    />
  );
});

const INITIAL_STATE = {
  brands: [],
  models: [],
  years: [],
  version: {},
};

const Nolose = ({ tipoSelected, vehicleData, setVehicleData }) => {
  const { currentUser } = useAuth();
  const { isAgentLogged } = useAgentAuth();
  const MIN_VAL = tipoSelected === "auto" ? 30_000 : 5_000;
  const inputRef = useRef(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [brandError, setBrandError] = useState(false);
  const [modelError, setModelError] = useState(false);
  const [yearError, setYearError] = useState(false);
  const [productCatalog, setProductCatalog] = useState(INITIAL_STATE);
  let whatsAppUrl = getWhatsAppUrl(
    "Hola, no encuentro mi vehículo en su catálogo."
  );

  const date = new Date();
  const currentYear = date.getFullYear();
  const isAProductWithYear =
    tipoSelected === "auto" ||
    tipoSelected === "moto" ||
    tipoSelected === "otro";
  const getType = (t) => {
    if (t === "bike") return "bikes";
    if (t === "eBike") return "eBikes";
    return t;
  };
  const typeSelected = getType(tipoSelected);

  useEffect(() => {
    const getVehicleBrands = async () => {
      try {
        const brandSnap = await db
          .collection(`catalogNew/${typeSelected}/brands`)
          .get();
        const brands = brandSnap.docs
          .map((doc) => doc.id)
          .filter(
            (b) => b.toLowerCase() !== "otro" && b.toLowerCase() !== "otros"
          );
        setProductCatalog((prev) => ({ ...prev, brands }));
      } catch (error) {
        console.error("Error getting brands:", error);
      }
    };

    getVehicleBrands();
  }, [tipoSelected]);

  useEffect(() => setProductCatalog(INITIAL_STATE), [tipoSelected]);

  useEffect(() => {
    const getVehicleModels = async () => {
      try {
        const modelSnap = await db
          .collection(
            `catalogNew/${typeSelected}/brands/${vehicleData.brand}/models`
          )
          .get();
        const models = modelSnap.docs
          .map((doc) => doc.id)
          .filter(
            (m) => m.toLowerCase() !== "otro" && m.toLowerCase() !== "otros"
          );
        setProductCatalog((prev) => ({ ...prev, models }));
      } catch (error) {
        console.error("Error getting models:", error);
      }
    };

    if (vehicleData.brand && vehicleData.brand !== "Otro") getVehicleModels();
  }, [vehicleData.brand, tipoSelected]);

  useEffect(() => {
    const getVehicleYears = async () => {
      try {
        const yearSnap = await db
          .collection(
            `catalogNew/${typeSelected}/brands/${vehicleData.brand}/models/${vehicleData.model}/years`
          )
          .get();
        const years = yearSnap.docs
          .map((doc) => doc.id)
          .filter(
            (y) =>
              String(y).toLowerCase() !== "otro" &&
              String(y).toLowerCase() !== "otros"
          )
          .filter((y) => {
            const year = parseInt(y);
            return year >= currentYear - 30 && year <= currentYear + 1;
          })
          .sort((a, b) => b - a);
        setProductCatalog((prev) => ({ ...prev, years }));
      } catch (error) {
        console.error("Error getting years:", error);
      }
    };

    if (vehicleData.model && vehicleData.model !== "Otro") getVehicleYears();
  }, [vehicleData.model, typeSelected, vehicleData.brand]);

  useEffect(() => {
    const getProductData = async () => {
      try {
        const basePath = `catalogNew/${typeSelected}/brands/${vehicleData.brand}/models/${vehicleData.model}`;

        let versionPath = isAProductWithYear
          ? `${basePath}/years/${vehicleData.year}`
          : basePath;

        const versionSnap = await db.doc(versionPath).get();
        if (!versionSnap.exists) return;

        const data = versionSnap.data();
        setProductCatalog((prev) => ({ ...prev, version: data }));

        const valuePrice = data.value ?? data.price;

        if (valuePrice) {
          setVehicleData((prev) => ({
            ...prev,
            value: valuePrice,
            valueCatalog: valuePrice,
            valueUser: valuePrice,
          }));
        }
      } catch (error) {
        console.error("Error fetching vehicle version:", error);
      }
    };

    if (vehicleData.model && (vehicleData.year || !isAProductWithYear)) {
      getProductData();
    }
  }, [vehicleData.model, vehicleData.year, tipoSelected, vehicleData.brand]);

  function formatNumberWithCommas(number) {
    return number.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  const handleChange = (e) => {
    const name = e.target.name;
    let value = e.target.value;
    if (name === "valueUser") {
      value = parseInt(value);
      setTimeout(() => setHasInteracted(true), 1500);
    }
    setVehicleData((prevDevice) => ({
      ...prevDevice,
      [name]: value,
    }));
  };

  const isPhoneValueValid =
    !!vehicleData.valueUser && parseInt(vehicleData.valueUser) >= MIN_VAL;

  const isVehicleValueOnCatalogValid = isValidProductValue({
    type: tipoSelected,
    value: vehicleData.valueCatalog,
  });

  const otherBrandOrModelSelected =
    vehicleData.brand === "Otro" || vehicleData.model === "Otro";

  const hasUserBrandOrModel = vehicleData?.userBrand || vehicleData?.userModel;

  const isAlternativeVehicleType = [
    "bike",
    "eBike",
    "scooter",
    "otro",
  ].includes(tipoSelected);

  const hasCluppId = productCatalog?.version?.cluppId;

  const isInvalidVehicleValue = !isVehicleValueOnCatalogValid;

  const vehicleYear = Number(vehicleData.year);

  const isVehicleNewOrInvalidValue =
    vehicleYear >= currentYear || isInvalidVehicleValue;

  const isNewAutoOrMoto =
    vehicleYear >= currentYear && ["auto", "moto"].includes(tipoSelected);

  const shouldAllowToChangeVehicleValue =
    isAlternativeVehicleType ||
    isNewAutoOrMoto ||
    hasUserBrandOrModel ||
    otherBrandOrModelSelected ||
    (hasCluppId && isVehicleNewOrInvalidValue);

  return (
    <Stack
      id="tipoVehiculo"
      sx={{
        margin: [
          "0px auto 5px auto",
          "0px auto 5px auto",
          "0px auto 5px auto",
          "-20px auto 5px auto",
        ],
        maxWidth: "650px",
      }}
    >
      <p className="bold">{`Ingresa los datos de tu ${getTypeName(
        tipoSelected
      )}`}</p>
      <FormControl variant="outlined" sx={{ m: 1, minWidth: 120 }} id="brand">
        <Autocomplete
          freeSolo={false} // No permite texto libre, solo opciones disponibles
          name="brand"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Marca"
              variant="outlined"
              error={brandError} // Muestra el error si es true
              helperText={
                brandError ? "No encontramos esta marca, selecciona 'Otro'" : ""
              }
            />
          )}
          value={vehicleData.brand}
          options={productCatalog.brands}
          onChange={(e, newValue) => {
            if (!newValue) {
              setBrandError(true); // Activa el error si no encuentra la marca
            } else {
              setBrandError(false);
              setVehicleData((prevDevice) => ({
                ...prevDevice,
                model: "",
                year: "",
                brand: newValue,
                value: "",
                valueCatalog: "",
                valueUser: "",
                userBrand: "",
                userModel: "",
              }));
              setProductCatalog((prev) => ({ ...prev, version: {} }));
              registerSelection({
                uid: currentUser.uid,
                productType: tipoSelected,
                brand: newValue,
                interactionType: "brand",
                isAgentLogged,
              });
            }
          }}
        />
      </FormControl>
      {vehicleData.brand === "Otro" && (
        <>
          <FormControl variant="outlined" sx={{ m: 1, minWidth: 120 }}>
            <TextField
              variant="outlined"
              label="Ingresa la marca"
              value={vehicleData.userBrand}
              onChange={handleChange}
              onBlur={(e) => {
                if (e.target.value) {
                  registerSelection({
                    uid: currentUser.uid,
                    productType: tipoSelected,
                    brand: e.target.value,
                    interactionType: "brand_other",
                    isAgentLogged,
                  });
                }
              }}
              name="userBrand"
            />
          </FormControl>
          <FormControl variant="outlined" sx={{ m: 1, minWidth: 120 }}>
            <TextField
              variant="outlined"
              label="Ingresa el modelo"
              value={vehicleData.userModel}
              onChange={handleChange}
              onBlur={(e) => {
                if (e.target.value) {
                  registerSelection({
                    uid: currentUser.uid,
                    productType: tipoSelected,
                    brand: vehicleData.userBrand,
                    model: e.target.value,
                    interactionType: "model_other",
                    isAgentLogged,
                  });
                }
              }}
              name="userModel"
            />
          </FormControl>
        </>
      )}
      {vehicleData.brand !== "Otro" && (
        <FormControl variant="outlined" sx={{ m: 1, minWidth: 120 }} id="model">
          <Autocomplete
            freeSolo={false} // No permite texto libre, solo opciones disponibles
            name="model"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Modelo"
                variant="outlined"
                error={modelError} // Muestra el error si es true
                helperText={
                  modelError
                    ? "No encontramos este modelo, selecciona 'Otro'"
                    : ""
                }
              />
            )}
            value={vehicleData.model}
            options={productCatalog.models}
            onChange={(e, newValue) => {
              if (!newValue) {
                setModelError(true); // Activa el error si no encuentra el modelo
              } else {
                setModelError(false);
                setVehicleData((prevDevice) => ({
                  ...prevDevice,
                  model: newValue,
                  year: "",
                  value: "",
                  valueCatalog: "",
                  valueUser: "",
                }));
                setProductCatalog((prev) => ({ ...prev, version: {} }));
                registerSelection({
                  uid: currentUser.uid,
                  productType: tipoSelected,
                  brand: vehicleData.brand,
                  model: newValue,
                  interactionType: "model",
                  isAgentLogged,
                });
              }
            }}
          />
        </FormControl>
      )}

      {vehicleData.model === "Otro" && (
        <FormControl variant="outlined" sx={{ m: 1, minWidth: 120 }}>
          <TextField
            variant="outlined"
            label="Ingresa el modelo"
            value={vehicleData.userModel}
            onChange={handleChange}
            onBlur={(e) => {
              if (e.target.value) {
                registerSelection({
                  uid: currentUser.uid,
                  productType: tipoSelected,
                  brand: vehicleData.brand,
                  model: e.target.value,
                  interactionType: "model_other",
                  isAgentLogged,
                });
              }
            }}
            name="userModel"
          />
        </FormControl>
      )}

      {isAProductWithYear && (
        <FormControl variant="outlined" sx={{ m: 1, minWidth: 120 }} id="year">
          <Autocomplete
            freeSolo={false} // No permite texto libre, solo opciones disponibles
            options={
              vehicleData.brand === "Otro" || vehicleData.model === "Otro"
                ? [
                    currentYear + 1,
                    currentYear,
                    ...Array.from(
                      { length: 30 },
                      (_, index) => currentYear - index
                    ),
                  ]
                : productCatalog?.years || []
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Año"
                variant="outlined"
                type="number"
                error={yearError} // Muestra el error si es true
                helperText={
                  yearError ? "No encontramos este año, selecciona 'Otro'" : ""
                }
              />
            )}
            value={vehicleData.year || ""}
            onChange={(e, newValue) => {
              if (!newValue) {
                setYearError(true); // Activa el error si no encuentra el año
              } else {
                setYearError(false);
                setVehicleData((prevData) => ({
                  ...prevData,
                  year: newValue,
                }));
                registerSelection({
                  uid: currentUser.uid,
                  productType: tipoSelected,
                  brand: vehicleData.brand,
                  model: vehicleData.model,
                  year: newValue,
                  interactionType: "year",
                  isAgentLogged,
                });
              }
            }}
          />
        </FormControl>
      )}

      <a
        style={{ color: "#0058f4", textDecoration: "underline" }}
        href={whatsAppUrl}
        target="_blank"
        rel="noreferrer"
      >
        ¿No encuentras tu vehículo?
      </a>

      {shouldAllowToChangeVehicleValue && (
        <div className="container-nolose">
          <div className="result">
            <p>
              {vehicleData.valueCatalog === 0 ||
              vehicleData.brand === "Otro" ||
              vehicleData.model === "Otro" ? (
                <>
                  Ingresa el valor comercial de tu {tipoSelected} <br />
                  <b>
                    {vehicleData.brand === "Otro"
                      ? vehicleData.userBrand
                      : vehicleData.brand}{" "}
                    {vehicleData.model === "Otro" ||
                    vehicleData.brand === "Otro"
                      ? vehicleData.userModel
                      : vehicleData.model}{" "}
                    {vehicleData?.year || ""}
                  </b>
                  :
                </>
              ) : (
                <>
                  Otros clientes han asegurado su <br />
                  <b>
                    {vehicleData.brand} {vehicleData.model}{" "}
                    {vehicleData.year || ""}
                  </b>{" "}
                  en promedio por un valor comercial de:
                </>
              )}
            </p>
            <FormControl
              variant="outlined"
              sx={{
                m: 1,
                minWidth: 120,
                marginTop: "20px",
                marginBottom: "25px",
              }}
              id="valueUser"
            >
              <TextField
                error={!isPhoneValueValid && hasInteracted}
                inputRef={inputRef}
                variant="outlined"
                label="Valor comercial"
                value={vehicleData.valueUser}
                onChange={handleChange}
                name="valueUser"
                InputProps={{
                  inputComponent: NumberFormatInput,
                }}
              />
              {!isPhoneValueValid && hasInteracted && (
                <FormHelperText style={{ color: "red", marginTop: "5px" }}>
                  {`El valor debe ser mayor a $${formatNumberWithCommas(
                    MIN_VAL
                  )}`}
                </FormHelperText>
              )}
            </FormControl>
          </div>
        </div>
      )}
    </Stack>
  );
};

export default Nolose;
